/* eslint-disable complexity */
import type { PayloadAction } from '@reduxjs/toolkit';
import { logger } from 'lib/logger';
import { AllDocumentsAccessRequestAPICallV4 } from 'reactApp/api/allDocumentsAccessRequestAPICall';
import { type IDocumentsApiArgs, DocumentsApiCall, DocumentsApiCallV4, SharedIncomingApiCall } from 'reactApp/api/DocumentsApiCall';
import { callWeblinksComplaintApiCall } from 'reactApp/api/weblinks/WeblinksComplaintApiCall';
import { weblinksIncomingApiCall } from 'reactApp/api/weblinks/WeblinksIncomingApiCall';
import { weblinksLinksApiCall } from 'reactApp/api/weblinks/WeblinksLinksApiCall';
import { IS_ALL_DOC_FEED_V4_ENABLED } from 'reactApp/appHelpers/featuresHelpers/features/alldocumentsFeedV4Enabled';
import {
    IS_ALL_DOC_SHARED_SORT_ENABLED,
    IS_ALL_DOC_SORT_ENABLED,
} from 'reactApp/appHelpers/featuresHelpers/features/alldocumentsSortEnabled';
import { B2B_ALLDOCUMENTS_REQUEST_INTERVAL } from 'reactApp/appHelpers/featuresHelpers/features/unpaidB2BDisableAllDocuments';
import {
    allDocumentsRadars,
    normalizeBaseFeedV4Item,
    normalizeBaseWeblinksListItem,
} from 'reactApp/modules/allDocuments/allDocuments.helpers';
import {
    allDocumentsLoadError,
    allDocumentsLoadRequest,
    allDocumentsLoadSuccess,
    allDocumentsMoreRequest,
    allDocumentsUnlockRequestSuccess,
    changeAllDocumentsCategory,
    checkAllDocumentsUnlockRequest,
    sendAllDocumentsUnlockRequest,
} from 'reactApp/modules/allDocuments/allDocuments.module';
import {
    getAllDocumentsCategory,
    getAllDocumentsCurrentType,
    getAllDocumentsCursor,
    getDocumentsState,
    getExtensionsForType,
} from 'reactApp/modules/allDocuments/allDocuments.selectors';
import {
    type DocumentComplaintRequest,
    type FeedApiV4Response,
    type WeblinksListApiV4Response,
    AllDocumentsCategoryType,
    AllDocumentsXrayTypes,
    EAllDocumentsType,
} from 'reactApp/modules/allDocuments/allDocuments.types';
import { normalizeBaseFeedItem } from 'reactApp/modules/feed/feed.helpers';
import type { FeedApiResponse, SharedFolderApiResponse } from 'reactApp/modules/feed/feed.types';
import { addHomeItemsToStore } from 'reactApp/modules/home/home.actions';
import type { HomeFile } from 'reactApp/modules/home/home.types';
import { normalizeIncomingPublicItem } from 'reactApp/modules/incomingPublic/incomingPublic.module';
import { handleLoadIncomingPublic } from 'reactApp/modules/incomingPublic/incomingPublic.saga';
import type { WeblinksIncomingApiResponse } from 'reactApp/modules/incomingPublic/incomingPublic.types';
import {
    makeNewDocumentError,
    makeNewDocumentSuccess,
    publishWeblink,
    unPublishWeblink,
} from 'reactApp/modules/modifying/modifying.actions';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { snackbarController } from 'reactApp/modules/snackbar/snackbar.controller';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import { defaultSortInAllDocs, getSortById } from 'reactApp/modules/sort/sort.selectors';
import { ESortTypes } from 'reactApp/modules/sort/sort.types';
import { getLoadMoreLimit } from 'reactApp/modules/storage/storage.helpers';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { reportDocument } from 'reactApp/modules/weblinkAccessControl/weblinkAccessControl.actions';
import {
    getAllDocumentsAccessRequestTimer,
    setAllDocumentsAccessRequestTimer,
} from 'reactApp/sections/AllDocuments/helpers/allDocumentsAccessRequestTimer';
import { checkTimeInterval } from 'reactApp/sections/AllDocuments/helpers/checkTimeInterval';
import { sendKaktamLog, sendXray } from 'reactApp/utils/ga';
import { captureException } from 'reactApp/utils/tracer';
import { put, takeEvery, takeLatest } from 'redux-saga/effects';
import { call, select } from 'typed-redux-saga';

const LOAD_LIMIT = getLoadMoreLimit(EStorageType.alldocuments);

const getAllDocumentsList = async ({ limit = LOAD_LIMIT, cursor = '', exts = '' }: IDocumentsApiArgs) =>
    (await new DocumentsApiCall().makeRequest({ limit, cursor, exts }))?.data?.body;

const getAllDocumentsListApiV4 = async ({
    limit = LOAD_LIMIT,
    cursor = '',
    exts = '',
    ignore_mounts = false,
    sort = defaultSortInAllDocs,
}: IDocumentsApiArgs) =>
    (await new DocumentsApiCallV4().makeRequest({ limit, cursor, exts, ignore_mounts, sort: sort?.type, order: sort?.order }))?.data;

const getMountedFolders = async () => (await new SharedIncomingApiCall().makeRequest())?.data?.body;

const getIncomingDocumentsList = async ({ limit = LOAD_LIMIT, cursor = '', exts = '', sort = undefined }: IDocumentsApiArgs) => {
    const replacedMtimeSort = sort?.type ? (sort.type === ESortTypes.mtime ? 'time' : sort.type) : undefined;
    return (await weblinksIncomingApiCall({ limit, cursor, exts, sort: replacedMtimeSort, order: sort?.order })).data;
};

const sendAllDocumentsAccessRequest = async () => {
    await new AllDocumentsAccessRequestAPICallV4().makeRequest();
};

function* handleLoadAllDocuments(
    action:
        | ReturnType<typeof allDocumentsLoadRequest>
        | ReturnType<typeof makeNewDocumentSuccess>
        | ReturnType<typeof changeAllDocumentsCategory>
        | ReturnType<typeof reportDocument>
        | ReturnType<typeof unPublishWeblink>
        | ReturnType<typeof publishWeblink>
) {
    const currentDocType = yield* select(getAllDocumentsCurrentType);
    const documentType =
        (action.payload && 'docType' in action.payload && action.payload.docType) || currentDocType || EAllDocumentsType.document;
    const extensions = yield* select(getExtensionsForType, documentType);
    const category =
        (action.payload && 'category' in action.payload && action.payload.category) || (yield* select(getAllDocumentsCategory));
    const isIgnoreMountsDocs = category === AllDocumentsCategoryType.onlyYours;
    const sort = yield select((state) => getSortById(state, documentType));
    const haveSort = IS_ALL_DOC_SORT_ENABLED ? sort : undefined;
    const isTriggeredByPublish = action.type === publishWeblink.toString() || action.type === unPublishWeblink.toString();

    if (extensions.length === 0) {
        return;
    }

    try {
        // Для фильтра "Вы поделились" запрашиваем личные веблинки и только
        if (category === AllDocumentsCategoryType.youShared) {
            const data = yield* call(weblinksLinksApiCall, {
                exts: extensions.join(','),
                limit: LOAD_LIMIT,
                sort: IS_ALL_DOC_SHARED_SORT_ENABLED ? haveSort : undefined,
            });
            yield* call(handleLoadListApiResponse, {
                documentType,
                dataWeblinksList: data,
                category,
                rewriteIdxs: true,
            });
            return;
        }

        // При publish \ unPublish нам нужно обновить только веблинки, остальные табы нет смысла обновлять + могут возникнуть проблемы
        if (isTriggeredByPublish) {
            return;
        }

        if (category === AllDocumentsCategoryType.sharedWithYou) {
            // В этой табе нет сортировки по размеру, но она есть в разделе, поэтому игнорим тут
            const sortInShared = IS_ALL_DOC_SHARED_SORT_ENABLED && haveSort && haveSort.type !== ESortTypes.size ? haveSort : undefined;
            const isComplaintAction = action.type === 'weblinks/email/access/complaint';
            const data = yield* call(getIncomingDocumentsList, { exts: extensions.join(','), limit: LOAD_LIMIT, sort: sortInShared });
            yield* call(handleLoadListApiResponse, {
                documentType,
                dataWeblinksIncomingList: data,
                category,
                rewriteIdxs: isComplaintAction,
            });
            return;
        }

        let feedV2Data: FeedApiResponse | undefined;
        let feedV4Data: FeedApiV4Response | undefined;
        if (IS_ALL_DOC_FEED_V4_ENABLED) {
            feedV4Data = yield* call(getAllDocumentsListApiV4, {
                exts: extensions.join(','),
                ignore_mounts: isIgnoreMountsDocs,
                sort: haveSort,
            });
        } else {
            feedV2Data = yield* call(getAllDocumentsList, { exts: extensions.join(',') });
        }

        const folders = yield* call(getMountedFolders);

        yield* call(handleLoadListApiResponse, {
            documentType,
            dataFeedV2: feedV2Data,
            dataFeedV4: feedV4Data,
            mountedFolders: folders,
            category,
        });
    } catch (error: any) {
        logger.error(error);

        sendXray(['page', 'error', 'alldocs'], documentType);
        if (haveSort) {
            sendXray(`alldocuments_error_sort_${documentType}_${sort.type}_${error.status ?? 'other'}`);
        }
        captureException(error, { message: 'alldocuments load saga' });
        sendKaktamLog(error, `page-error-alldocs-${documentType}${haveSort ? `-${sort.type}` : ''}`);

        yield put(allDocumentsLoadError({ type: documentType, category, error: error.toString() }));
    }
}

function* handleLoadMoreAllDocuments() {
    const documentType = yield* select(getAllDocumentsCurrentType);
    const category = yield* select(getAllDocumentsCategory);
    const extensions = yield* select(getExtensionsForType, documentType);
    const cursor = yield* select(getAllDocumentsCursor);
    const isIgnoreMountsDocs = category === AllDocumentsCategoryType.onlyYours;
    const sort = yield select((state) => getSortById(state, documentType || EAllDocumentsType.document));
    const haveSort = IS_ALL_DOC_SORT_ENABLED ? sort : undefined;

    if (extensions.length === 0) {
        return;
    }

    try {
        // Для фильтра "Вы поделились" запрашиваем личные веблинки и только
        if (category === AllDocumentsCategoryType.youShared) {
            const data = yield* call(weblinksLinksApiCall, {
                exts: extensions.join(','),
                limit: LOAD_LIMIT,
                cursor,
                sort: IS_ALL_DOC_SHARED_SORT_ENABLED ? haveSort : undefined,
            });
            yield* call(handleLoadListApiResponse, {
                documentType: documentType || EAllDocumentsType.document,
                dataWeblinksList: data,
                category,
            });
            return;
        }

        if (category === AllDocumentsCategoryType.sharedWithYou) {
            const sortInShared = IS_ALL_DOC_SHARED_SORT_ENABLED && haveSort.type !== ESortTypes.size ? haveSort : undefined;
            const data = yield* call(getIncomingDocumentsList, {
                exts: extensions.join(','),
                limit: LOAD_LIMIT,
                cursor,
                sort: sortInShared,
            });
            yield* call(handleLoadListApiResponse, {
                documentType: documentType || EAllDocumentsType.document,
                dataWeblinksIncomingList: data,
                category,
            });
            return;
        }

        let feedV2Data: FeedApiResponse | undefined;
        let feedV4Data: FeedApiV4Response | undefined;
        if (IS_ALL_DOC_FEED_V4_ENABLED) {
            feedV4Data = yield* call(getAllDocumentsListApiV4, {
                exts: extensions.join(','),
                cursor,
                ignore_mounts: isIgnoreMountsDocs,
                sort: haveSort,
            });
        } else {
            feedV2Data = yield* call(getAllDocumentsList, { exts: extensions.join(','), cursor });
        }

        yield* call(handleLoadListApiResponse, {
            documentType: documentType || EAllDocumentsType.document,
            dataFeedV2: feedV2Data,
            dataFeedV4: feedV4Data,
            category,
        });
    } catch (error: any) {
        logger.error(error);
        sendXray(['page', 'error', 'alldocs'], documentType);
        if (haveSort) {
            sendXray(['alldocuments', 'error', 'sort', documentType, sort.type], {
                ...(error.status ? { [error.status]: 1 } : {}),
            });
        }
        captureException(error, { message: 'alldocuments load more saga' });
        sendKaktamLog(error, `page-error-alldocs-${documentType}${haveSort ? `-${sort.type}` : ''}`);
        yield put(allDocumentsLoadError({ type: documentType, category, error: error.toString() }));
    }
}

function* handleLoadListApiResponse({
    dataFeedV2,
    dataFeedV4,
    documentType,
    category,
    dataWeblinksList,
    dataWeblinksIncomingList,
    mountedFolders,
    rewriteIdxs,
}: {
    documentType: EAllDocumentsType;
    category: AllDocumentsCategoryType;
    dataFeedV2?: FeedApiResponse;
    dataFeedV4?: FeedApiV4Response;
    dataWeblinksList?: WeblinksListApiV4Response;
    dataWeblinksIncomingList?: WeblinksIncomingApiResponse;
    mountedFolders?: SharedFolderApiResponse;
    rewriteIdxs?: boolean;
}) {
    const items: HomeFile[] = [];
    const idxs: string[] = [];
    let docsSize = 0;

    dataWeblinksIncomingList?.links?.forEach((apiItem) => {
        const item = {
            ...normalizeIncomingPublicItem(apiItem),
            storage: EStorageType.incomingPublic,
            isMounted: false,
        };

        items.push(item as HomeFile);
        idxs.push(item.id);
    });

    dataWeblinksList?.list?.forEach((apiItem) => {
        const item = {
            ...normalizeBaseWeblinksListItem(apiItem),
            storage: EStorageType.home,
            isMounted: false,
        };
        docsSize += item.size;
        items.push(item as HomeFile);
        idxs.push(item.id);
    });

    dataFeedV4?.list?.forEach((apiItem) => {
        const item = {
            ...normalizeBaseFeedV4Item(apiItem),
            storage: EStorageType.home,
            isMounted: false,
        };
        docsSize += item.size;
        items.push(item as HomeFile);
        idxs.push(item.id);
    });

    dataFeedV2?.objects?.forEach((apiItem) => {
        const item = {
            ...normalizeBaseFeedItem(apiItem),
            storage: EStorageType.home,
            isMounted: false,
        };
        docsSize += item.size;
        items.push(item as HomeFile);
        idxs.push(item.id);
    });

    mountedFolders?.list?.forEach((apiItem) => {
        const item = {
            ...normalizeBaseFeedItem(apiItem),
            storage: EStorageType.home,
            isMounted: true,
            isFolder: true,
            kind: 'mounted',
            isReadOnly: apiItem.access === 'read_only',
        };
        items.push(item as HomeFile);
    });

    if (items.length) {
        yield put(addHomeItemsToStore(items));
    }

    yield put(
        allDocumentsLoadSuccess({
            type: documentType,
            data: dataFeedV4 || dataFeedV2 || dataWeblinksList || dataWeblinksIncomingList,
            idxs,
            docsSize,
            category,
            rewriteIdxs,
        })
    );
}

/**
 * Генератор для отправки технических метрик для раздела документов.
 *
 * @param {string} action Наименование действия.
 * @param {AllDocumentsXrayTypes} xrayType Тип радара.
 * @param {documentType} [documentType] Кастомный тип документа, который можно прокинуть вручную. По умолчанию используется тип в зависимости от текущего раздела документов.
 */
export function* handleSendAllDocumentsXray({
    action,
    xrayType,
    documentType = null,
}: {
    action: string;
    xrayType: AllDocumentsXrayTypes;
    documentType?: EAllDocumentsType | null;
}) {
    const storage = yield select(getCurrentStorage);

    if (storage === EStorageType.alldocuments) {
        const { currentDocType } = yield select(getDocumentsState);

        const radars = allDocumentsRadars({ section: documentType ?? currentDocType, action });

        yield radars[xrayType]();
    }
}

export function* handleSendCreatedDocumentSuccessXray() {
    const { currentDocType } = yield select(getDocumentsState);

    yield handleSendAllDocumentsXray({
        action: 'create_file',
        xrayType: AllDocumentsXrayTypes.onSuccess,
        documentType: currentDocType,
    });
}

export function* handleSendCreatedDocumentErrorXray() {
    const { currentDocType } = yield select(getDocumentsState);

    yield handleSendAllDocumentsXray({
        action: 'create_file',
        xrayType: AllDocumentsXrayTypes.onError,
        documentType: currentDocType,
    });
}

function* handleSendAllDocumentsUnlockRequest() {
    try {
        yield call(sendAllDocumentsAccessRequest);

        yield put(allDocumentsUnlockRequestSuccess());

        yield put(setAllDocumentsAccessRequestTimer(Date.now()));

        sendXray('unpaid-b2b-alldocs-enable-request-sent');

        yield call(snackbarController.showSnackbar, {
            type: SnackbarTypes.success,
            text: 'Запрос отправлен администратору',
            id: 'allDocumentsAccessRequestSent',
            closable: true,
        });
    } catch (error) {
        logger.error(error);
        sendXray('unpaid-b2b-alldocs-enable-request-error');
        captureException(error, { issueKey: 'alldocuments page error' });
    }
}

function* handleCheckAllDocumentsUnlockRequest() {
    const lastTimestamp = yield put(getAllDocumentsAccessRequestTimer());

    if (checkTimeInterval(lastTimestamp, B2B_ALLDOCUMENTS_REQUEST_INTERVAL)) {
        yield put(allDocumentsUnlockRequestSuccess());
    }
}

function* handleReportDocumentRequest({ payload }: PayloadAction<DocumentComplaintRequest>) {
    const { weblink_id, source } = payload;

    try {
        yield call(callWeblinksComplaintApiCall, { weblink_id });

        sendXray(`${source}-document_complaint_request_sent`);

        if (source === 'sharedWithYou') {
            yield call(handleLoadAllDocuments, reportDocument(payload));
        } else {
            yield call(handleLoadIncomingPublic);
        }
    } catch (error) {
        logger.error(error);
        sendXray(`${source}-document_complaint_request_error`);
    }
}

export function* watchAllDocuments() {
    yield takeEvery(allDocumentsLoadRequest.toString(), handleLoadAllDocuments);
    yield takeEvery(allDocumentsMoreRequest.toString(), handleLoadMoreAllDocuments);
    yield takeEvery(changeAllDocumentsCategory.toString(), handleLoadAllDocuments);
    yield takeEvery(makeNewDocumentSuccess.toString(), handleLoadAllDocuments);
    yield takeEvery(makeNewDocumentSuccess.toString(), handleSendCreatedDocumentSuccessXray);
    yield takeEvery(makeNewDocumentError.toString(), handleSendCreatedDocumentErrorXray);
    yield takeEvery(sendAllDocumentsUnlockRequest.toString(), handleSendAllDocumentsUnlockRequest);
    yield takeEvery(checkAllDocumentsUnlockRequest, handleCheckAllDocumentsUnlockRequest);
    yield takeEvery(reportDocument.toString(), handleReportDocumentRequest);
    yield takeLatest([publishWeblink.toString(), unPublishWeblink.toString()], handleLoadAllDocuments);
}
